import classNames from 'classnames';
import { DataLayerProps } from 'eco-design-micro/props/DataLayerProps';
import gtm from 'eco-design-reliance/gtm';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import IconArrow from './icon/IconArrow';
import './index.scss';

export interface EcoTabItemProps {
    /**
     * 对应 activeKey
     */
    tabValue: string;
    /**
     * 选项卡头显示文字
     */
    tabLabel: React.ReactNode;
    /**
     * 样式名
     */
    className?: string;
    /**
     * 埋点
     */
    tabDataLayer?: DataLayerProps;
}

export interface EcoTabProps {
    /**
     * 页签的基本样式
     *
     * default 通用默认
     *
     * square 方形
     *
     * round 圆形
     *
     * leftAndBorder 左侧且底部有边框线
     */
    type?: 'default' | 'square' | 'round' | 'leftAndBorder';
    /**
     * 主题色
     */
    theme?: 'white' | 'black';
    /**
     * 默认选中
     */
    activeKey?: string;
    /**
     * 列表
     */
    items: EcoTabItemProps[];
    /**
     * 默认选中第几个
     */
    activeIndex?: number;
    /**
     * 样式名
     */
    className?: string;
    /**
     * list_name (gtm)
     */
    list_name?: string;
    /**
     * 切换面板的回调
     */
    onChange?: (item: EcoTabItemProps, index: number) => void;

    gtmTitle?: string;
}

/**
 *
 * 左右切换 tab
 */
const EcoTab: React.FC<EcoTabProps> = ({ theme, type = 'default', activeKey, gtmTitle, activeIndex = 0, items = [], className, onChange }) => {
    const prefixCls = `eco-tab`;
    const [showLRIcon, setShowLRIcon] = useState(false); // 是否展示左右箭头
    const tabRef = useRef();
    const classes = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-theme-${theme}`]: theme,
        [`${prefixCls}-type-${type}`]: type,
        [`${prefixCls}-lr-show`]: showLRIcon,
    });
    const classes2 = classNames(`${prefixCls}-items`, {
        [`${prefixCls}-items-type-${type}`]: type,
    });
    const classes3 = classNames(`${prefixCls}-items-move`, {
        [`${prefixCls}-items-move-type-${type}`]: type,
    });

    // 根据 activeKey 选中
    const initIndex1 = items.findIndex((item: EcoTabItemProps) => {
        return item.tabValue === activeKey;
    });
    const initIndex2 = initIndex1 > -1 ? initIndex1 : activeIndex;
    const [selectIndex, setSelectIndex] = useState(initIndex2 || 0);

    // 点击切换
    const toggleHandle = (item: EcoTabItemProps, index: number) => {
        if (index === selectIndex) {
            return;
        }
        setSelectIndex(index);
        if (onChange) {
            onChange(item, index);
        }
        try {
            const dataLayer = item.tabDataLayer || {
                event: 'uaEvent',
                event_name: 'tab_click',
                list_name: gtmTitle || 'none',
                button_name: item.tabLabel,
            };
            gtm.push(dataLayer);
        } catch (e) { }
    };

    // 点击向左
    const scrollToLHandle = () => {
        if (!tabRef.current) {
            return false;
        }
        const elemOut = tabRef.current.querySelector('.eco-tab-items');
        const elemIn = tabRef.current.querySelector('.eco-tab-items-move');
        if (!elemOut || !elemIn) {
            return false;
        }
        elemOut.scrollBy(-100, 0);
    };
    // 点击向右
    const scrollToRHandle = () => {
        if (!tabRef.current) {
            return false;
        }
        const elemOut = tabRef.current.querySelector('.eco-tab-items');
        const elemIn = tabRef.current.querySelector('.eco-tab-items-move');
        if (!elemOut || !elemIn) {
            return false;
        }
        elemOut.scrollBy(100, 0);
    };

    useEffect(() => {
        // 根据宽度是否展示左右
        const setLRShowHandle = () => {
            if (!tabRef.current) {
                return false;
            }
            const elemOut = tabRef.current.querySelector('.eco-tab-items');
            const elemIn = tabRef.current.querySelector('.eco-tab-items-move');
            if (!elemOut || !elemIn) {
                return false;
            }
            const widthOut = elemOut.clientWidth;
            const widthIn = elemIn.clientWidth;
            if (widthIn > widthOut) {
                setShowLRIcon(true);
            } else {
                setShowLRIcon(false);
            }
        };
        setLRShowHandle();
    }, []);

    useEffect(() => {
        if (activeIndex !== selectIndex) {
            setSelectIndex(activeIndex);
        }
    }, [activeIndex]);
    return (
        <div className={classes} ref={tabRef}>
            <div className={`${prefixCls}-arrow-icon ${prefixCls}-arrow-icon-left`} onClick={scrollToLHandle}>
                <IconArrow />
            </div>
            <div className={classes2}>
                <div className={classes3}>
                    {items?.map((item: EcoTabItemProps, index: number) => {
                        const classItem = classNames(`${prefixCls}-item`, {
                            [`${item.className}`]: item.className,
                            [`${prefixCls}-item-active`]: selectIndex === index,
                        });
                        return (
                            <Fragment key={index}>
                                {index === 0 && <div className={`${prefixCls}-space`}></div>}
                                <div
                                    className={classItem}
                                    onClick={(e) => {
                                        toggleHandle(item, index);
                                        if (e.target) {
                                            (e.target as HTMLDivElement).scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                                        }
                                    }}
                                >
                                    {item.tabLabel}
                                </div>
                                <div className={`${prefixCls}-space`}></div>
                            </Fragment>
                        );
                    })}
                </div>
            </div>
            <div className={`${prefixCls}-arrow-icon ${prefixCls}-arrow-icon-right`} onClick={scrollToRHandle}>
                <IconArrow />
            </div>
        </div>
    );
};

export default EcoTab;
